import { useCallback } from "react";
import { View } from "@common/types/views/views";
import { useTaskColumnSizingCache } from "./useTaskColumnSizingCache";

/**
 * Merges cached column sizes into a view's columns.
 * Only columns present in the view are updated; columns without a cached size
 * are returned unchanged.
 */
function applyColumnSizes<TView extends View>(
    view: TView,
    columnSizes: Record<string, number>,
): TView {
    if (Object.keys(columnSizes).length === 0) {
        return view;
    }
    return {
        ...view,
        columns: view.columns.map((col) => {
            const size = columnSizes[col.id];
            if (size === undefined) {
                return col;
            }
            return { ...col, options: { ...col.options, size } };
        }),
    };
}

interface UseTaskColumnResizingResult {
    applyColumnSizesToView: <TView extends View>(view: TView) => TView;
    onViewChange: (updatedView: View) => void;
}

export function useTaskColumnResizing(
    taskId: string,
): UseTaskColumnResizingResult {
    const [columnSizes, setColumnSizes] = useTaskColumnSizingCache(taskId);

    const applyColumnSizesToView = useCallback(
        <TView extends View>(view: TView) =>
            applyColumnSizes(view, columnSizes),
        [columnSizes],
    );

    const onViewChange = useCallback(
        (updatedView: View) => {
            const newSizes: Record<string, number> = {};
            for (const col of updatedView.columns) {
                const size = col.options?.size;
                if (size !== undefined) {
                    newSizes[col.id] = size;
                }
            }
            setColumnSizes({ ...columnSizes, ...newSizes });
        },
        [columnSizes, setColumnSizes],
    );

    return { applyColumnSizesToView, onViewChange };
}
